/**
 * Avis « pas de siège » en tête de la liste des projets (mode `registre`, lot O3).
 *
 * Un membre actif sans siège ne reçoit, dans les listes (`GET /projects`, `/teams`, portefeuille),
 * que les projets de son espace personnel : ceux de l'organisation sont filtrés sans bruit, et
 * l'écran semblerait simplement vide de ce qu'il attend. Cet avis le dit et mène à
 * l'organisation, où l'on peut demander un siège. Selon le rôle, le premier accès à l'organisation
 * tente l'auto-siège (éditeur, lecteur : par les membres ; propriétaire, administrateur : par une
 * sonde gardée) ; `facturation` n'en reçoit jamais.
 *
 * Facultatif par nature : toute erreur est silencieuse (la liste de projets n'en dépend pas) et
 * le mode `local` ne fait aucune requête — l'écran y reste strictement celui d'avant le lot.
 */

import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api.js';
import { useAsync } from '../hooks/useAsync.js';
import { useOrganisationStudio } from '../hooks/useOrganisationStudio.js';
import { BTN_GHOST_SM } from '../ui/theme.js';

/** Aiguillage par mode : le composant qui charge n'est monté qu'en mode `registre`. */
export function SeatNotice() {
  return useOrganisationStudio() === 'registre' ? <SeatNoticeRegistre /> : null;
}

function SeatNoticeRegistre() {
  const { t } = useTranslation();
  const { data } = useAsync(async (signal) => {
    const { data: organisations } = await api.get('/me/organizations', { signal });
    // ORG-4 : au plus une organisation ; seule une adhésion active est soumise au siège.
    const organisation = (organisations ?? []).find((item) => item.status === 'active');
    if (!organisation) return null;
    const { data: resume } = await api.get(`/organizations/${organisation.id}`, { signal });
    return resume?.siege === false
      ? { id: organisation.id, name: resume.name ?? organisation.name }
      : null;
  }, []);

  if (!data) return null;
  return (
    <div className="border-b-2 border-line">
      <div
        role="status"
        className="mx-auto flex w-full max-w-(--largeur-corps) flex-wrap items-center justify-between gap-2 px-4 py-3 text-sm text-ink-soft"
      >
        <span>{t('seatNotice.text', { name: data.name })}</span>
        <Link to={`/organisation/${data.id}`} className={BTN_GHOST_SM}>
          {t('seatNotice.link')}
        </Link>
      </div>
    </div>
  );
}
